import React, { useEffect, useState } from 'react';
import { CustomPaper } from '../styles';
import { FormControl, IconButton, InputLabel, MenuItem, Select, Typography, Grid, Button, Snackbar, Alert } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import Error from '../../../components/Error/Error';
import { request } from '../../../interfaces/networkManager';
import PlanetCreationVersionModal from '../components/PlanetCreationVersionModal';
import { getCurrentUrl } from '../../../services/ApiServices';
import PlanetTable from '../components/PlanetTable';
import { PlanetDeleteVersionModal } from '../components/PlanetDeleteVersionModal';
import { headers } from '../../../services/genericConstantes';
import { Version } from '../../../models/version';

const PlanetPart = () => {
	const [versions, setVersions] = useState<Version[]>([]);
	const [version, setVersion] = useState<Version | null>(null);
	const [selectedVersion, setSelectedVersion] = useState('');
	const [isCreationVersionModalOpen, setIsCreationVersionModalOpen] = useState(false);
	const [isDeleteVersionConfirmOpen, setIsDeleteVersionConfirmOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successSnackOpen, setSuccessSnackOpen] = useState(false);

	useEffect(() => {
		request<Version[]>('/mating-plan-objective-versions')
			.then((response: Version[]) => {
				if (!response.length) return setError('Erreur lors de la récupération des objectifs');
				setVersions(response);
			})
			.catch((error: string) => {
				setError('Erreur lors de la récupération des objectifs');
			});
	}, []);

	useEffect(() => {
		if (selectedVersion && selectedVersion !== '' && versions) {
			const versionToSave = versions.find((v) => v.name === selectedVersion) || null;
			setVersion(versionToSave);
		}
	}, [selectedVersion, versions]);

	const handleCreation = async (name: string, code: string) => {
		const postOptions = {
			headers: headers(),
			method: 'POST',
			body: JSON.stringify({ name, code }),
		};

		fetch(`${getCurrentUrl()}/mating-plan-objective-versions`, postOptions)
			.then(async (response: any) => {
				const responseJson = await response.json();
				if (responseJson.id) {
					setVersions([...versions, responseJson]);
					setIsCreationVersionModalOpen(false);
					setSuccessSnackOpen(true);
				} else {
					return setError("Erreur lors de la duplication de l'objectif");
				}
			})
			.catch((error: string) => {
				setError("Erreur lors de la duplication de l'objectif");
			});
	};

	const handleDuplicate = async (name: string, code: string) => {
		if (version) {
			const postOptions = {
				headers: headers(),
				method: 'POST',
				body: JSON.stringify({ name, code }),
			};

			fetch(`${getCurrentUrl()}/mating-plan-objective-versions/${version.id}/duplicate`, postOptions)
				.then(async (response: any) => {
					const responseJson = await response.json();
					if (responseJson.id) {
						setVersions([...versions, responseJson]);
						setIsCreationVersionModalOpen(false);
						setSuccessSnackOpen(true);
					} else {
						return setError("Erreur lors de la duplication de l'objectif");
					}
				})
				.catch((error: string) => {
					setError("Erreur lors de la duplication de l'objectif");
				});
		}
	};

	const handleClear = () => {
		setSelectedVersion('');
		setVersion(null);
	};

	const handleDelete = async () => {
		if (version) {
			const deleteOptions = {
				headers: headers(),
				method: 'DELETE',
			};

			fetch(`${getCurrentUrl()}/mating-plan-objective-versions/${version.id}`, deleteOptions)
				.then(async (response: any) => {
					if (response.ok) {
						setVersions(versions.filter((v) => v.id !== version.id));
						setVersion(null);
						setIsDeleteVersionConfirmOpen(false);
						setSuccessSnackOpen(true);
					} else {
						return setError("Erreur lors de la duplication de l'objectif");
					}
				})
				.catch((error: string) => {
					setError("Erreur lors de la duplication de l'objectif");
				});
		}
	};

	return (
		<CustomPaper sx={{ minHeight: '25rem' }}>
			<Snackbar open={successSnackOpen} autoHideDuration={4000} onClose={() => setSuccessSnackOpen(false)}>
				<Alert severity="success">Modifications appliquées avec succès</Alert>
			</Snackbar>
			<PlanetCreationVersionModal id={4} onValidate={selectedVersion ? handleDuplicate : handleCreation} isOpen={isCreationVersionModalOpen} setIsOpen={setIsCreationVersionModalOpen} />

			<PlanetDeleteVersionModal isOpen={isDeleteVersionConfirmOpen} setIsOpen={setIsDeleteVersionConfirmOpen} onValidate={handleDelete} />

			<Grid container spacing={1}>
				<Grid size={{ xs: 12 }}>
					<Typography variant="h5" color="primary" gutterBottom align="left">
						Inventaire des objectifs PLANET :
					</Typography>
				</Grid>
				{error && <Error message={error} />}
				<Grid size={{ xs: 8 }}>
					<FormControl error={Boolean(error)} fullWidth>
						<InputLabel id="versions">Versions d'objectifs</InputLabel>
						<Select
							labelId="versions-label"
							label="Versions d'objectifs"
							value={selectedVersion}
							onChange={(e: any) => {
								setSelectedVersion(e.target.value);
							}}
							endAdornment={
								selectedVersion && (
									<IconButton onClick={handleClear} sx={{ marginRight: '0.5rem' }} aria-label="Réinitialiser les objectifs">
										<ClearIcon />
									</IconButton>
								)
							}
						>
							{versions.map((s: Version) => (
								<MenuItem key={s.id} value={s.name}>
									{s.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 4 }}>
					<Grid container spacing={1} sx={{ justifyContent: "center", alignItems: "center", height: '100%' }}>
						<Grid size={{ xs: 5 }}>
							{selectedVersion ? (
								<Button variant="outlined" disabled={!selectedVersion} onClick={() => setIsCreationVersionModalOpen(true)} aria-label="Dupliquer la version sélectionnée">
									Dupliquer la version sélectionnée
								</Button>
							) : (
								<Button variant="outlined" color="success" onClick={() => setIsCreationVersionModalOpen(true)} aria-label="Créer une nouvelle version">
									Créer une nouvelle version
								</Button>
							)}
						</Grid>
						<Grid size={{ xs: 5 }}>
							<Button variant="outlined" disabled={!selectedVersion} fullWidth color="error" sx={{ height: '100%' }} onClick={() => setIsDeleteVersionConfirmOpen(true)} aria-label="Effacer la version sélectionnée">
								Effacer la version sélectionnée
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid size={{ xs: 12 }}>
					{!!version && <PlanetTable version={version} setError={setError} setSuccessSnackOpen={setSuccessSnackOpen} />}
				</Grid>
			</Grid>
		</CustomPaper>
	);
};

export default PlanetPart;
